import React, { useCallback, useId, useState } from 'react'
import { ReactComponent as FilterIcon } from '@assets/images/DataTable/filter.svg'
import type { SearchProps } from './Search.types'
import {
  SEARCH_PREVIEW_FILLED_VALUE,
  SEARCH_PREVIEW_PLACEHOLDER,
} from './Search.types'
import './Search.css'

const Search: React.FC<SearchProps> = ({
  value,
  defaultValue = '',
  placeholder = SEARCH_PREVIEW_PLACEHOLDER,
  disabled = false,
  showFilter = false,
  allowClearWithFilter = false,
  filterExpanded = false,
  filterButtonRef,
  darkMode = false,
  className = '',
  ariaLabel = 'Search',
  previewState,
  onChange,
  onClear,
  onFilterClick,
}) => {
  const inputId = useId()
  const [internalValue, setInternalValue] = useState(defaultValue)
  const [isFocused, setIsFocused] = useState(false)

  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isFilledPreview = previewState === 'filled'
  const isFilterPreview = previewState === 'with-filter'
  const resolvedValue = isPreview
    ? isFilledPreview
      ? SEARCH_PREVIEW_FILLED_VALUE
      : ''
    : (value ?? internalValue)
  const hasValue = resolvedValue.length > 0
  const showClearButton =
    (isPreview && isFilledPreview) ||
    (!isPreview &&
      hasValue &&
      (allowClearWithFilter || (!showFilter && !isFilterPreview)))
  const showFilterButton = showFilter || isFilterPreview

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const nextValue = event.target.value

      if (value === undefined) {
        setInternalValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [onChange, value],
  )

  const handleClear = useCallback((): void => {
    if (value === undefined) {
      setInternalValue('')
    }

    onChange?.('')
    onClear?.()
  }, [onChange, onClear, value])

  const classNames = [
    'ds-search',
    darkMode ? 'ds-search--dark' : '',
    isDisabled ? 'ds-search--disabled' : '',
    isPreview ? 'ds-search--preview' : '',
    previewState ? `ds-search--preview-${previewState}` : '',
    !isPreview && isFocused ? 'ds-search--focused' : '',
    !isPreview && hasValue ? 'ds-search--filled' : '',
    showFilterButton ? 'ds-search--with-filter' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      <div className="ds-search__field">
        <div className="ds-search__leading">
          <i
            className="mgc mgc_search_line ds-search__icon"
            aria-hidden="true"
          />
          <label className="ds-search__label" htmlFor={inputId}>
            {ariaLabel}
          </label>
          <input
            id={inputId}
            type="search"
            className="ds-search__input"
            value={resolvedValue}
            placeholder={placeholder}
            aria-label={ariaLabel}
            disabled={isDisabled}
            readOnly={isPreview}
            tabIndex={isPreview ? -1 : undefined}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>

        {showClearButton ? (
          <button
            type="button"
            className="ds-search__clear-btn"
            aria-label="Clear search"
            disabled={isDisabled}
            onClick={handleClear}
          >
            <i className="mgc mgc_close_line" aria-hidden="true" />
          </button>
        ) : null}

        {showFilterButton ? (
          <button
            ref={filterButtonRef}
            type="button"
            className="ds-search__filter-btn"
            aria-label="Filter"
            aria-expanded={filterExpanded}
            aria-haspopup="dialog"
            disabled={isDisabled}
            tabIndex={isPreview ? -1 : undefined}
            onClick={onFilterClick}
          >
            <FilterIcon aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default Search
